import { useState, useEffect, useContext } from 'react';
import { Circles } from 'react-loader-spinner';
import { ReactSVG } from 'react-svg';
import { getLocations, saveLocation, formatLocationAddress, getUserRolesForLocation } from './utils';
import { findGmapsState, findGmapsCountry, findGmapsCity, getTimezone } from '../../utils';
import { getAuth0Users } from '../Users/utils';
import { createSublocation } from '../Journey/utils';
import { Context } from '../Context';

import plusIcon from '../../images/icons/screens/white-plus.svg';
import saveIcon from '../../images/icons/menu/save.svg';

import Subscriptions from './Subscriptions';

const LocationPage = ({ lang, userData, customer }) => {
    const [state, setState] = useContext(Context);
    userData = state.userData;
    customer = state.customer

    const [locations, setLocations] = useState([]);
    const [editLocation, setEditLocation] = useState({
        address1:"",
        name: "",
        purchasedProducts: []
    });
    const [sortConfig, setSortConfig] = useState({ sortBy: 'name', direction: 'asc' });
    const [isLocationEdited, setIsLocationEdited] = useState(false);
    const [isCreatedLocation, setIsCreatedLocation] = useState(false);
    const [users, setUsers] = useState([]);
    const [userRoles, setUserRoles] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(false);

    const getBizLocations = async () => {
        return await getLocations(customer.id);
    }

    const getUsers = async () => {
        return await getAuth0Users(customer.id);
    }

    const getUserRoles = async (id) => {
        return await getUserRolesForLocation(id);
    }

    useEffect(() => {
        getBizLocations().then(data => {
            setLocations(data);
        })
        getUsers().then(users => {
            setUsers(users);
        })
    }, []);

    useEffect(() => {
        if (editLocation?.id) {
            getUserRoles(editLocation.id).then(userRoles => {
                setUserRoles(userRoles);
            })
        }
    }, [editLocation.id])

    const locationClicked = (location) => {
        setEditLocation(location);
        setIsCreatedLocation(false);
    }

    const createNewLocation = async () => {
        let purchasedProducts = [];
        if (locations.length > 1) {
            // Copy foresight userRoles/permissions from the latest modified location
            let location = locations.reduce((prev, current) => {
                return new Date(prev.updatedAt) > new Date(current.updatedAt) ? prev : current
            });
            purchasedProducts = { foresight: location.purchasedProducts.foresight }

            // We need to set the pre-existing foresight userRoles here to make it clear that
            // foresight userRoles are applied across all locations, even new ones.
            let allUserRoles = await getUserRoles(location.id);
            if ("foresight" in allUserRoles) {
                let result = {
                    "foresight": Object.fromEntries(
                        Object.entries(allUserRoles.foresight).map(([role, users]) => [
                            role,
                            users.map(({ name, role, subscription, userId, locationId }) => ({ name, role, subscription, userId, locationId: null }))
                        ])
                    )
                };
                setUserRoles(result);
            }
        }
        setEditLocation({
            address1:"",
            name: "",
            customerId: customer.id,
            purchasedProducts: purchasedProducts //purchasedProducts
        })
        setIsCreatedLocation(true);
    }

    const handleLocationChange = (prop, value) => {
        const tempLocation = {...editLocation};
        tempLocation[prop] = value;
        setEditLocation(tempLocation);
        setIsLocationEdited(true);
    }

    const formatDateDigits = (num) => {
        return num < 10 ? `0${num}` : num;
    }

    const formatDate = (dateTime) => {
        const date = dateTime ? new Date(dateTime) : null
        return date
        ? `${formatDateDigits(date.getMonth() + 1)}/${formatDateDigits(date.getDate())}/${date.getFullYear()}`
        : null
    }

    const fillSvgColor = (svg, stroke, fill) => {
		if (stroke || fill) {
			for (var i = 0; i < svg.children.length; i++) {
                svg.children[i].setAttribute('stroke', stroke);
				svg.children[i].setAttribute('fill', fill);
            }
		}
	}

    function handleSort(column) {
        setSortConfig(prev => {
            if (prev.sortBy === column) {
                // Same column clicked soflip direction
                return {
                    sortBy: column,
                    direction: prev.direction === 'asc' ? 'desc' : 'asc'
                };
            } else {
                // New column clicked so reset to ascending
                return {
                    sortBy: column,
                    direction: 'asc'
                };
            }
        });
      }

    const sortLocations = (locations, { sortBy, direction }) => {
        return [...locations].sort((a, b) => {
            let comparison = typeof a[sortBy] === 'number' && typeof b[sortBy] === 'number'
                ? a[sortBy] - b[sortBy]
                : String(a[sortBy]).localeCompare(String(b[sortBy]));

            return direction === 'asc' ? comparison : -comparison;
        });
    }

    const handleSubmit = async () => {

        setLoading(true);

        await saveLocation(editLocation, userRoles)
        .then(async response => {
            //Success
            if (response.status === 201) {
                await getBizLocations().then(async data => {
                    setLocations(data);
                    setState((prevState) => {
                        return { ...prevState, locations: data }
                    });
                    const newEditLocation = data.find(loc => loc.address1 === editLocation.address1 && loc.name === editLocation.name);
                    setEditLocation(newEditLocation)

                    if (isCreatedLocation && newEditLocation) {
                        // New locations get a matching final sublocation so Journey has
                        // somewhere to route to without requiring a separate setup step.
                        try {
                            await createSublocation({
                                name: newEditLocation.name,
                                locationId: newEditLocation.id,
                                address1: newEditLocation.address1,
                                address2: '',
                                city: newEditLocation.city,
                                state: newEditLocation.state,
                                zip: newEditLocation.zip,
                                country: newEditLocation.country,
                                latitude: newEditLocation.latitude,
                                longitude: newEditLocation.longitude,
                                timezone: newEditLocation.timezone,
                                isPreset: false,
                                isFinal: true,
                                availableFrom: null,
                                availableTo: null,
                            });
                        } catch (e) {
                            console.error('Failed to create default sublocation', e);
                        }
                    }
                })
            }
            else {
                console.log("Could not create or update the location");
            }
        });

        setLoading(false);
        setError(false);
        setIsLocationEdited(false);
    }

    const locationsRows = locations.length === 0
	? <tr className="no-locations-text"><td></td><td></td><td></td><td>No locations found</td></tr>
	: sortLocations(locations, sortConfig).map(location => {
		return <tr key={location.id} className={`location-row`} onClick={() => locationClicked(location)}>
            <td>{location?.name}</td>
            <td>{location?.address1}</td>
            <td>{location?.city}</td>
            <td>{location?.state}</td>
            <td>{location?.zip}</td>
            <td>{location?.country}</td>
            <td>{formatDate(location.createdAt)}</td>
		</tr>
	})

	return ( 
		<div className="LocationsPage">
            { loading && <LoadingModal /> }
            <div className="actions-tab">
                <div className="left-container">
                    <button className="open-location pure-u-1-9" onClick={createNewLocation}>
                        <img className="open-icon" src={plusIcon}/>
                    </button>
                    <div className="location-name pure-u-1-7">
                        <p style={{marginTop: 0, marginBottom: 0}}>{customer?.name}</p>
                        <p style={{marginTop: "0.5em"}}>{customer?.address1}</p>
                    </div>
                </div>
                <div className={`icon-container ${isLocationEdited ? "changed" : ""}`} title="Save changes" onClick={() => handleSubmit(editLocation)}>
                    <ReactSVG className="icon" src={saveIcon} beforeInjection={(svg) => isLocationEdited ? fillSvgColor(svg, null, '#fff') : null}/>
                </div>
			</div>
            <div className="locations-container">
                <h3>Locations</h3>
                <div className="locations-table-wrapper">
                    <table className="locations-table">
                        <thead>
                            <tr className="header-row">
                                <th className="table-header" onClick={() => handleSort('name')}>Name</th>
                                <th className="table-header" onClick={() => handleSort('address1')}>Address</th>
                                <th className="table-header" onClick={() => handleSort('city')}>City</th>
                                <th className="table-header" onClick={() => handleSort('state')}>State/Province</th>
                                <th className="table-header" onClick={() => handleSort('zip')}>Zip/Post</th>
                                <th className="table-header" onClick={() => handleSort('country')}>Country</th>
                                <th className="table-header" onClick={() => handleSort('createdAt')}>Created</th>
                            </tr>
                        </thead>
                        <tbody className="table-body">
                            {locationsRows}
                        </tbody>
                    </table>    
                </div>            
            </div>
            <Subscriptions isCreatedLocation={isCreatedLocation} location={editLocation} setEditLocation={setEditLocation} handleLocationChange={handleLocationChange} userRoles={userRoles} setUserRoles={setUserRoles} users={users} setIsLocationEdited={setIsLocationEdited} customer={customer} userData={userData} />
		</div>
	);
}

const LoadingModal = () => {

    return (
        <div className="Loading">
            <Circles
                height="80"
                width="80"
                color="#2386EE"
                ariaLabel="circles-loading"
                visible={true}
            />
        </div>
    )
}

export default LocationPage;
